import { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useTicketCard = (ticketId) => {
  const [card, setCard] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCard = useCallback(async () => {
    if (!ticketId) return;
    
    setLoading(true);
    try {
      const { data } = await api.get(`/kanban/cards/ticket/${ticketId}`);
      setCard(data);
      setError(null);
    } catch (err) {
      console.log(`Nenhum cartão encontrado para o ticket ${ticketId}`);
      // Não exibimos erro aqui porque o ticket pode não ter um cartão ainda
      setCard(null);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  const fetchStages = useCallback(async (queueId) => {
    if (!queueId) return;
    
    try {
      const { data } = await api.get(`/kanban/stages/queue/${queueId}`);
      setStages(data);
      return { data }; // Retornar os dados para que o componente possa usar
    } catch (err) {
      console.error(err);
      setStages([]);
      return { data: [] }; // Retornar um array vazio em caso de erro
    }
  }, []);

  useEffect(() => {
    fetchCard();
  }, [fetchCard, ticketId]);

  // Quando encontramos um cartão, buscamos os estágios da fila associada
  useEffect(() => {
    if (card && card.queueId) {
      fetchStages(card.queueId);
    }
  }, [card, fetchStages]);

  const createCard = async (queueId, userId) => {
    if (!ticketId || !queueId) {
      throw new Error("QueueId e TicketId são necessários para criar um cartão");
    }

    try {
      // Buscar estágios da fila do ticket
      const { data: stagesData } = await fetchStages(queueId);
      const { data: ticketData } = await api.get(`/tickets/${ticketId}`);
      const contact = ticketData.contact; 


      // Verificar se há estágios configurados
      if (!stagesData || stagesData.length === 0) {
        toastError({
          message: "Não há estágios configurados para esta fila. Configure os estágios do Kanban primeiro."
        });
        throw new Error("Não há estágios configurados para esta fila");
      }

      const initialStage = stagesData.find(stage => stage.isInitial);
      
      if (!initialStage) {
        // Exibir mensagem de erro para o usuário
        toastError({
          message: "Nenhum estágio inicial configurado para esta fila. Configure os estágios do Kanban primeiro."
        });
        throw new Error("Nenhum estágio inicial configurado para esta fila");
      }
      
      // Agora temos certeza que initialStage existe e tem um id
      const { data } = await api.post("/kanban/cards", {
        title: `#${ticketId} - ${contact.name}`,
        description: "", // Adicionando campo description com valor vazio para garantir que não seja nulo
        ticketId,
        queueId,
        userId,
        stageId: initialStage.id,
      });
      
      setCard(data);
      return data;
    } catch (err) {
      console.error(err);
      toastError(err);
      throw err;
    }
  };

  const updateCard = async (formData) => {
    if (!card || !card.id) {
      throw new Error("Nenhum cartão para atualizar");
    }

    try {
      const { data } = await api.put(`/kanban/cards/${card.id}`, formData);
      setCard(data);
      return data;
    } catch (err) {
      console.error(err);
      //toastError(err);
      throw err;
    }
  };

  const moveCard = async (stageId) => {
    if (!card || !card.id) {
      throw new Error("Nenhum cartão para mover");
    }

    try {
      const { data } = await api.put(`/kanban/cards/${card.id}/move`, {
        stageId
      });
      setCard(data);
      return data;
    } catch (err) {
      console.error(err);
      toastError(err);
      throw err;
    }
  };

  const refreshCard = () => {
    fetchCard();
  };

  // Função para adicionar uma mensagem ao contexto do cartão
  const addMessageToContext = async (messageId) => {
    if (!card || !card.id) {
      throw new Error("Nenhum cartão disponível para adicionar contexto");
    }

    try {
      console.log(`[DEBUG] Adicionando mensagem ${messageId} ao contexto do cartão ${card.id}`);
      
      // Chamar a API para adicionar a mensagem ao contexto do cartão
      const { data } = await api.post(`/kanban/cards/${card.id}/messages`, {
        messageId
      });
      
      // Atualizar o cartão localmente com a nova mensagem adicionada
      setCard(prevCard => {
        // Se o cartão já tem cardMessages
        if (prevCard.cardMessages) {
          // Verificar se a mensagem já existe para evitar duplicação
          const messageExists = prevCard.cardMessages.some(cm => cm.messageId === messageId);
          
          if (!messageExists) {
            return {
              ...prevCard,
              cardMessages: [...prevCard.cardMessages, data]
            };
          }
          return prevCard;
        } else {
          // Inicializar o array de cardMessages
          return {
            ...prevCard,
            cardMessages: [data]
          };
        }
      });
      
      return data;
    } catch (err) {
      console.error("Erro ao adicionar mensagem ao contexto:", err);
      throw err;
    }
  };

  // Função para remover uma mensagem do contexto do cartão
  const removeMessageFromContext = async (messageId) => {
    if (!card || !card.id) {
      throw new Error("Nenhum cartão disponível para remover contexto");
    }

    try {
      console.log(`[DEBUG] Removendo mensagem ${messageId} do contexto do cartão ${card.id}`);
      
      // Buscar o ID da relação cartão-mensagem para poder excluir
      const cardMessageToDelete = card.cardMessages?.find(cm => cm.messageId === messageId);
      
      if (!cardMessageToDelete) {
        throw new Error("Mensagem não encontrada no contexto do cartão");
      }
      
      // Chamar a API para remover a mensagem do contexto do cartão
      await api.delete(`/kanban/cards/messages/${cardMessageToDelete.id}`);
      
      // Atualizar o cartão localmente removendo a mensagem excluída
      setCard(prevCard => {
        return {
          ...prevCard,
          cardMessages: prevCard.cardMessages.filter(cm => cm.messageId !== messageId)
        };
      });
      
      return true;
    } catch (err) {
      console.error("Erro ao remover mensagem do contexto:", err);
      throw err;
    }
  };

  return {
    card,
    stages,
    loading,
    error,
    createCard,
    updateCard,
    moveCard,
    refreshCard,
    fetchStages,
    addMessageToContext,
    removeMessageFromContext,
    setCard  // Adicionando o setter para uso nos componentes
  };
};

export default useTicketCard;